import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  downloadAccountingPayablePdf,
  getAccountingSuppliersOptions,
  listAccountingPayables,
  updateAccountingPayableStatus,
} from "../../api/accountingApi";
import "./AdminAccountingPagamentiFornitori.css";

function centsToEur(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "0,00";
  return (n / 100).toFixed(2).replace(".", ",");
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function formatFiscalType(v) {
  if (v === "piva") return "P.IVA";
  if (v === "cf") return "Codice fiscale";
  return "Sconosciuto";
}

function formatPaymentStatus(v) {
  if (v === "pagato") return "Pagato";
  if (v === "parzialmente_pagato") return "Parzialmente pagato";
  return "Da pagare";
}

function ymdToDisplay(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function displayToYmd(display) {
  if (!display) return "";
  const clean = String(display).trim();
  const m = clean.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;

  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";

  if (
    d.getFullYear() !== Number(yyyy) ||
    d.getMonth() + 1 !== Number(mm) ||
    d.getDate() !== Number(dd)
  ) {
    return "";
  }

  return `${yyyy}-${mm}-${dd}`;
}

function normalizeDateTyping(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function InlineDateInput({ value, onChange, disabled }) {
  const [draft, setDraft] = useState(ymdToDisplay(value));
  const nativeRef = useRef(null);

  useEffect(() => {
    setDraft(ymdToDisplay(value));
  }, [value]);

  const openPicker = () => {
    if (disabled) return;
    if (nativeRef.current?.showPicker) {
      nativeRef.current.showPicker();
    } else {
      nativeRef.current?.focus();
      nativeRef.current?.click();
    }
  };

  const handleTextChange = (e) => {
    const next = normalizeDateTyping(e.target.value);
    setDraft(next);

    const ymd = displayToYmd(next);
    onChange(ymd);
  };

  const handleBlur = () => {
    const ymd = displayToYmd(draft);

    if (!draft.trim()) {
      setDraft("");
      onChange("");
      return;
    }

    if (ymd) {
      setDraft(ymdToDisplay(ymd));
      onChange(ymd);
      return;
    }

    setDraft(value ? ymdToDisplay(value) : "");
  };

  return (
    <div className="accp-date-wrap">
      <input
        type="text"
        inputMode="numeric"
        className="accp-input accp-input--inline accp-input--date-text"
        value={draft}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder="gg/mm/aaaa"
        disabled={disabled}
        aria-label="Data pagamento"
      />

      <button
        type="button"
        className="accp-date-btn"
        onClick={openPicker}
        disabled={disabled}
        title="Apri calendario"
        aria-label="Apri calendario"
      >
        📅
      </button>

      <input
        ref={nativeRef}
        type="date"
        className="accp-native-date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}

function formatSourceType(row) {
  const orders = Number(row?.order_count) || 0;
  const subs = Number(row?.subscription_count) || 0;

  if (orders > 0 && subs > 0) return `Mista: ${orders} ord. · ${subs} abb.`;
  if (subs > 0) return `Abbonamenti: ${subs}`;
  return orders > 0 ? `Ordini: ${orders}` : "—";
}

export default function AdminAccountingPagamentiFornitori() {
  const [filters, setFilters] = useState({
    year: "",
    fornitore_id: "",
  });

  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const hasActiveFilters = !!(filters.year || filters.fornitore_id);

  const [sort, setSort] = useState({
    field: "invoice_number_label",
    dir: "desc",
  });

  const [editingId, setEditingId] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState({
    supplier_payment_status: "da_pagare",
    supplier_payment_date: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);

  async function loadSuppliers() {
    try {
      setLoadingSuppliers(true);
      const data = await getAccountingSuppliersOptions();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[PagamentiFornitori] loadSuppliers error:", err);
      toast.error(err?.message || "Errore caricamento fornitori");
    } finally {
      setLoadingSuppliers(false);
    }
  }

  async function loadData(nextPage = 1, overrideFilters) {
    const effFilters = overrideFilters || filters;
    try {
      setLoading(true);
      const res = await listAccountingPayables({
        page: nextPage,
        pageSize,
        filters: effFilters,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
      setPage(res.page || nextPage);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error("[PagamentiFornitori] loadData error:", err);
      toast.error(err?.message || "Errore caricamento pagamenti fornitori");
    } finally {
      setLoading(false);
    }
  }

  const startEditPayment = (row) => {
    setEditingId(row.id);
    setPaymentDraft({
      supplier_payment_status: row.supplier_payment_status || "da_pagare",
      supplier_payment_date: row.supplier_payment_date || "",
    });
  };

  const handleResetFilters = () => {
    const cleared = {
      year: "",
      fornitore_id: "",
    };

    setFilters(cleared);
    loadData(1, cleared);
  };

  const cancelEditPayment = () => {
    setEditingId(null);
    setPaymentDraft({
      supplier_payment_status: "da_pagare",
      supplier_payment_date: "",
    });
  };

  const savePaymentStatus = async (invoiceId) => {
    try {
      setSavingPayment(true);

      await updateAccountingPayableStatus(invoiceId, {
        supplier_payment_status: paymentDraft.supplier_payment_status,
        supplier_payment_date: paymentDraft.supplier_payment_date || null,
      });

      toast.success("Stato pagamento aggiornato");
      cancelEditPayment();
      await loadData(page, filters);
    } catch (err) {
      console.error("[PagamentiFornitori] savePaymentStatus error:", err);
      toast.error(err?.message || "Errore salvataggio stato pagamento");
    } finally {
      setSavingPayment(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
    loadData(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    const nextFilters = { ...filters, [field]: value };
    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const handleSort = (field) => {
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const renderSortIcon = (field) => {
    if (!sort || sort.field !== field) {
      return <span className="accp-sort-icon">↕</span>;
    }
    return (
      <span className="accp-sort-icon accp-sort-icon--active">
        {sort.dir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { field, dir } = sort || {};

    const getVal = (r) => {
      switch (field) {
        case "invoice_number_label":
          return {
            year: Number(r.year) || 0,
            number: Number(r.number) || 0,
            date: r.invoice_date || "",
            id: Number(r.id) || 0,
          };
        case "invoice_date":
          return r.invoice_date || "";
        case "fornitore_label":
          return r.fornitore_label || "";
        case "supplier_fiscal_type":
          return r.supplier_fiscal_type || "";
        case "orders_gross_cents":
          return Number(r.orders_gross_cents) || 0;
        case "ns_ft_cents":
          return Number(r.ns_ft_cents) || 0;
        case "withholding_cents":
          return Number(r.withholding_cents) || 0;
        case "supplier_net_cents":
          return Number(r.supplier_net_cents) || 0;
        default:
          return r.invoice_date || "";
      }
    };

    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      if (field === "invoice_number_label") {
        if (va.year !== vb.year) {
          return dir === "asc" ? va.year - vb.year : vb.year - va.year;
        }

        if (va.number !== vb.number) {
          return dir === "asc" ? va.number - vb.number : vb.number - va.number;
        }

        const ta = va.date ? new Date(va.date).getTime() : 0;
        const tb = vb.date ? new Date(vb.date).getTime() : 0;
        if (ta !== tb) {
          return dir === "asc" ? ta - tb : tb - ta;
        }

        return dir === "asc" ? va.id - vb.id : vb.id - va.id;
      }

      if (field === "invoice_date") {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;

        if (ta !== tb) {
          return dir === "asc" ? ta - tb : tb - ta;
        }

        const ya = Number(a.year) || 0;
        const yb = Number(b.year) || 0;
        if (ya !== yb) {
          return dir === "asc" ? ya - yb : yb - ya;
        }

        const na = Number(a.number) || 0;
        const nb = Number(b.number) || 0;
        if (na !== nb) {
          return dir === "asc" ? na - nb : nb - na;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      if (typeof va === "number" && typeof vb === "number") {
        if (va !== vb) {
          return dir === "asc" ? va - vb : vb - va;
        }

        const ida = Number(a.id) || 0;
        const idb = Number(b.id) || 0;
        return dir === "asc" ? ida - idb : idb - ida;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();

      if (sa !== sb) {
        if (sa < sb) return dir === "asc" ? -1 : 1;
        return dir === "asc" ? 1 : -1;
      }

      const ida = Number(a.id) || 0;
      const idb = Number(b.id) || 0;
      return dir === "asc" ? ida - idb : idb - ida;
    });

    return arr;
  }, [rows, sort]);

  const totals = useMemo(() => {
    return sortedRows.reduce(
      (acc, row) => {
        acc.orders_gross_cents += Number(row.orders_gross_cents) || 0;
        acc.ns_ft_cents += Number(row.ns_ft_cents) || 0;
        acc.withholding_cents += Number(row.withholding_cents) || 0;
        acc.supplier_net_cents += Number(row.supplier_net_cents) || 0;
        return acc;
      },
      {
        orders_gross_cents: 0,
        ns_ft_cents: 0,
        withholding_cents: 0,
        supplier_net_cents: 0,
      },
    );
  }, [sortedRows]);

  const openPdf = async (invoiceId) => {
    if (!invoiceId) return;

    try {
      await downloadAccountingPayablePdf(invoiceId);
    } catch (err) {
      console.error("[PagamentiFornitori] downloadPayablePdf error:", err);
      toast.error(err?.message || "Errore di rete durante il download del PDF");
    }
  };

  return (
    <div className="accp-page">
      <div className="accp-inner">
        <header className="accp-header">
          <div>
            <h1>Contabilità · Pagamenti fornitori</h1>
            <p className="accp-header-sub">
              Qui controlli quanto deve essere liquidato ai fornitori per ogni
              fattura già emessa, distinguendo tra P.IVA e codice fiscale con
              ritenuta d’acconto.
            </p>
          </div>
        </header>

        <section className="accp-filters">
          <div className="accp-filters-row">
            <div className="accp-filter-group">
              <label className="accp-label">Anno</label>
              <input
                type="number"
                className="accp-input"
                value={filters.year}
                onChange={handleFilterChange("year")}
                placeholder="Es. 2026"
              />
            </div>

            <div className="accp-filter-group">
              <label className="accp-label">Fornitore</label>
              <select
                className="accp-input"
                value={filters.fornitore_id}
                onChange={handleFilterChange("fornitore_id")}
                disabled={loadingSuppliers}
              >
                <option value="">Tutti</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="accp-filter-group accp-filter-group--inline">
              <button
                type="button"
                className="accp-btn accp-btn--ghost"
                onClick={handleResetFilters}
                disabled={loading || !hasActiveFilters}
              >
                Reset filtri
              </button>
            </div>
          </div>
        </section>

        <section className="accp-table-section">
          <div className="accp-table-wrapper">
            <table className="accp-table">
              <thead>
                <tr>
                  <th
                    className="accp-th accp-th--date accp-th--sortable"
                    onClick={() => handleSort("invoice_date")}
                  >
                    <div className="accp-th-content">
                      <span className="accp-th-label">Data</span>
                      {renderSortIcon("invoice_date")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--ft accp-th--sortable"
                    onClick={() => handleSort("invoice_number_label")}
                  >
                    <div className="accp-th-content">
                      <span className="accp-th-label">FT</span>
                      {renderSortIcon("invoice_number_label")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--supplier accp-th--sortable"
                    onClick={() => handleSort("fornitore_label")}
                  >
                    <div className="accp-th-content">
                      <span className="accp-th-label">Fornitore</span>
                      {renderSortIcon("fornitore_label")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--fiscal accp-th--sortable"
                    onClick={() => handleSort("supplier_fiscal_type")}
                  >
                    <div className="accp-th-content">
                      <span className="accp-th-label">Regime</span>
                      {renderSortIcon("supplier_fiscal_type")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--num accp-th--sortable"
                    onClick={() => handleSort("orders_gross_cents")}
                  >
                    <div className="accp-th-content accp-th-content--right">
                      <span className="accp-th-label">Tot. lordo</span>
                      {renderSortIcon("orders_gross_cents")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--num accp-th--sortable"
                    onClick={() => handleSort("ns_ft_cents")}
                  >
                    <div className="accp-th-content accp-th-content--right">
                      <span className="accp-th-label">Ns FT</span>
                      {renderSortIcon("ns_ft_cents")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--num accp-th--sortable"
                    onClick={() => handleSort("withholding_cents")}
                  >
                    <div className="accp-th-content accp-th-content--right">
                      <span className="accp-th-label">Ritenuta</span>
                      {renderSortIcon("withholding_cents")}
                    </div>
                  </th>

                  <th
                    className="accp-th accp-th--num accp-th--sortable"
                    onClick={() => handleSort("supplier_net_cents")}
                  >
                    <div className="accp-th-content accp-th-content--right">
                      <span className="accp-th-label">Netto documento</span>
                      {renderSortIcon("supplier_net_cents")}
                    </div>
                  </th>
                  <th className="accp-th accp-th--paystatus">
                    <div className="accp-th-content">
                      <span className="accp-th-label">Stato pagamento</span>
                    </div>
                  </th>

                  <th className="accp-th accp-th--paydate">
                    <div className="accp-th-content">
                      <span className="accp-th-label">Data pagamento</span>
                    </div>
                  </th>
                  <th className="accp-th accp-th--actions">Azioni</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr className="accp-row accp-row--loading">
                    <td colSpan={11}>Caricamento dati…</td>
                  </tr>
                )}

                {!loading && sortedRows.length === 0 && (
                  <tr className="accp-row accp-row--empty">
                    <td colSpan={11}>
                      Nessuna fattura trovata con i filtri correnti.
                    </td>
                  </tr>
                )}

                {!loading &&
                  sortedRows.map((r) => (
                    <tr key={r.id} className="accp-row">
                      <td className="accp-cell accp-cell--date">
                        {formatDate(r.invoice_date)}
                      </td>

                      <td className="accp-cell accp-cell--ft">
                        <span className="accp-mono">
                          {r.invoice_number_label}
                        </span>
                        <br />
                        <span style={{ fontSize: 10, color: "#9ca3af" }}>
                          {formatSourceType(r)}
                        </span>
                      </td>

                      <td className="accp-cell accp-cell--supplier">
                        {r.fornitore_label || "—"}
                      </td>

                      <td className="accp-cell accp-cell--fiscal">
                        <span
                          className={`accp-pill accp-pill--${
                            r.supplier_fiscal_type || "unknown"
                          }`}
                        >
                          {formatFiscalType(r.supplier_fiscal_type)}
                        </span>
                      </td>

                      <td className="accp-cell accp-cell--num">
                        {centsToEur(r.orders_gross_cents)} €
                      </td>

                      <td className="accp-cell accp-cell--num">
                        {centsToEur(r.ns_ft_cents)} €
                      </td>

                      <td className="accp-cell accp-cell--num">
                        {centsToEur(r.withholding_cents)} €
                      </td>

                      <td className="accp-cell accp-cell--num accp-cell--net">
                        {centsToEur(r.supplier_net_cents)} €{r.supplier_payment_status === "parzialmente_pagato" && <small style={{ display: "block" }}>Stato storico da riconciliare: importo parziale non registrato</small>}
                      </td>

                      <td className="accp-cell accp-cell--paystatus">
                        {editingId === r.id ? (
                          <select
                            className="accp-input accp-input--inline"
                            value={paymentDraft.supplier_payment_status}
                            onChange={(e) =>
                              setPaymentDraft((prev) => ({
                                ...prev,
                                supplier_payment_status: e.target.value,
                              }))
                            }
                            disabled={savingPayment}
                          >
                            <option value="da_pagare">Da pagare</option>
                            {r.supplier_payment_status === "parzialmente_pagato" && <option value="parzialmente_pagato" disabled>Parziale storico — da riconciliare</option>}
                            <option value="pagato">Pagato</option>
                          </select>
                        ) : (
                          <span
                            className={`accp-pill accp-pill--payment accp-pill--payment-${
                              r.supplier_payment_status || "da_pagare"
                            }`}
                          >
                            {formatPaymentStatus(r.supplier_payment_status)}
                          </span>
                        )}
                      </td>

                      <td className="accp-cell accp-cell--paydate">
                        {editingId === r.id ? (
                          <InlineDateInput
                            value={paymentDraft.supplier_payment_date || ""}
                            onChange={(val) =>
                              setPaymentDraft((prev) => ({
                                ...prev,
                                supplier_payment_date: val,
                              }))
                            }
                            disabled={
                              savingPayment ||
                              paymentDraft.supplier_payment_status ===
                                "da_pagare"
                            }
                          />
                        ) : (
                          <span>
                            {r.supplier_payment_date
                              ? formatDate(r.supplier_payment_date)
                              : "—"}
                          </span>
                        )}
                      </td>

                      <td className="accp-cell accp-cell--actions">
                        <div className="accp-actions">
                          <button
                            type="button"
                            className="accp-btn accp-btn--ghost accp-btn--sm"
                            onClick={() => openPdf(r.id)}
                            disabled={savingPayment}
                          >
                            PDF
                          </button>

                          {editingId === r.id ? (
                            <>
                              <button
                                type="button"
                                className="accp-btn accp-btn--primary accp-btn--sm"
                                onClick={() => savePaymentStatus(r.id)}
                                disabled={savingPayment}
                              >
                                {savingPayment ? "Salvataggio..." : "Salva"}
                              </button>

                              <button
                                type="button"
                                className="accp-btn accp-btn--ghost accp-btn--sm"
                                onClick={cancelEditPayment}
                                disabled={savingPayment}
                              >
                                Annulla
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="accp-btn accp-btn--ghost accp-btn--sm"
                              onClick={() => startEditPayment(r)}
                              disabled={savingPayment}
                            >
                              Modifica
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="accp-totals-row">
            <div className="accp-totals-panel">
              <div className="accp-totals-line">
                <span className="accp-totals-line-label">
                  Totali filtro corrente:
                </span>

                <span className="accp-totals-chip">
                  <span className="accp-totals-chip-label">Tot. ordini</span>
                  <span className="accp-totals-chip-value">
                    {centsToEur(totals.orders_gross_cents)} €
                  </span>
                </span>

                <span className="accp-totals-chip">
                  <span className="accp-totals-chip-label">Ns FT</span>
                  <span className="accp-totals-chip-value">
                    {centsToEur(totals.ns_ft_cents)} €
                  </span>
                </span>

                <span className="accp-totals-chip">
                  <span className="accp-totals-chip-label">Ritenuta</span>
                  <span className="accp-totals-chip-value">
                    {centsToEur(totals.withholding_cents)} €
                  </span>
                </span>

                <span className="accp-totals-chip accp-totals-chip--strong">
                  <span className="accp-totals-chip-label">
                    Netto documenti (non residuo)
                  </span>
                  <span className="accp-totals-chip-value accp-totals-chip-value--strong">
                    {centsToEur(totals.supplier_net_cents)} €
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="accp-bottom-actions">
            <div className="accp-pagination">
              <button
                type="button"
                className="accp-btn accp-btn--ghost"
                disabled={loading || page <= 1}
                onClick={() => loadData(page - 1)}
              >
                ← Pagina precedente
              </button>

              <span className="accp-page-info">
                Pagina {page} di {totalPages}
              </span>

              <button
                type="button"
                className="accp-btn accp-btn--ghost"
                disabled={loading || page >= totalPages}
                onClick={() => loadData(page + 1)}
              >
                Pagina successiva →
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
